import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { updatePoiData, updateDiscoData, updateMspData, updateMbeData, updateBlpData, updateAtsData, updateProductionMeterData, updateUtilityData, updateSubpanelData } from '../../../../../store/slices/createCRSlice';
import { selectPoiData, selectDiscoData, selectDisco1Data, selectDisco2Data, selectDisco3Data, selectDisco4Data, selectDisco5Data, selectMspData, selectMbeData, selectBlpData, selectAtsData, selectProductionMeterData, selectUtilityData, selectSubpanelData } from '../../../../../store/selectors/createCRSelectors';
import { discoDetailsService } from '../../../../../services';
import { DiscoDetails } from '../../../../../types';
import { 
  selectPoi, 
  selectPoiLocations,
  selectMspBusses,
  selectMspBreakers,
  selectMspManufacturers,
  selectMspLocations,
  selectMspCadLocations,
  selectMspStyles,
  selectMspTypes,
  selectElectricConfigs,
  selectDisco
} from '../../../../../store/selectors/databaseSelectors';
import { POISelector } from './POISelector';
import { MSPSelector } from './MSPSelector';
import { MBESelector } from './MBESelector';
import { BLPSelector } from './BLPSelector';
import { ATSSelector } from './ATSSelector';
import { ProductionMeterSelector } from './ProductionMeterSelector';
import { UtilitySelector } from './UtilitySelector';
import { SubpanelSelector } from './SubpanelSelector';
import { DISCOSelector } from './DISCOSelector';

interface ElectricalStepProps {
  isEnabled: boolean;
}

export const ElectricalStep: React.FC<ElectricalStepProps> = ({ isEnabled }) => {
  const dispatch = useAppDispatch();
  const [discoDetails, setDiscoDetails] = useState<DiscoDetails[]>([]);
  
  // Redux selectors
  const poiData = useAppSelector(selectPoiData);
  const disco1Data = useAppSelector(selectDisco1Data);
  const disco2Data = useAppSelector(selectDisco2Data);
  const disco3Data = useAppSelector(selectDisco3Data);
  const disco4Data = useAppSelector(selectDisco4Data);
  const disco5Data = useAppSelector(selectDisco5Data);
  const mspData = useAppSelector(selectMspData);
  const mbeData = useAppSelector(selectMbeData);
  const blpData = useAppSelector(selectBlpData);
  const atsData = useAppSelector(selectAtsData);
  const productionMeterData = useAppSelector(selectProductionMeterData);
  const utilityData = useAppSelector(selectUtilityData);
  const subpanelData = useAppSelector(selectSubpanelData);
  const poi = useAppSelector(selectPoi);
  const poiLocations = useAppSelector(selectPoiLocations);
  const mspBusses = useAppSelector(selectMspBusses);
  const mspBreakers = useAppSelector(selectMspBreakers);
  const mspManufacturers = useAppSelector(selectMspManufacturers);
  const mspLocations = useAppSelector(selectMspLocations);
  const mspCadLocations = useAppSelector(selectMspCadLocations);
  const mspStyles = useAppSelector(selectMspStyles);
  const mspTypes = useAppSelector(selectMspTypes);
  const electricConfigs = useAppSelector(selectElectricConfigs);
  const disco = useAppSelector(selectDisco);
  const { is_initialized } = useAppSelector((state) => state.database);

  // Initialize enabledDiscos based on existing data in Redux
  const [enabledDiscos, setEnabledDiscos] = useState<number[]>(() => {
    const discoDataArray = [disco1Data, disco2Data, disco3Data, disco4Data, disco5Data];
    const enabled: number[] = [];
    
    discoDataArray.forEach((discoData, index) => {
      // Check if this disco has any meaningful data
      if (discoData?.rating || discoData?.type || discoData?.model) {
        enabled.push(index + 1);
      }
    });
    
    // If no discos have data, start with disco 1 enabled
    return enabled.length > 0 ? enabled : [1];
  });

  // Fetch disco details
  useEffect(() => {
    const fetchDiscoDetails = async () => {
      if (discoDetails.length === 0) {
        try {
          const details = await discoDetailsService.getAll();
          setDiscoDetails(details);
        } catch (error) {
          console.error('Error fetching disco details:', error);
        }
      }
    };

    fetchDiscoDetails();
  }, [discoDetails.length]);

    return (
    <div className="flex-1 overflow-y-auto px-2 py-4 min-h-0">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-amber-400 overflow-hidden h-full">
            <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-amber-100/50 border-b border-slate-200/60">
              <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">MSP</span>
            </div>
            <div className="p-5 h-full flex flex-col">
          <MSPSelector
            selectedMspBuss={mspData.buss_rating}
            selectedMspBreaker={mspData.main_breaker_rating}
            selectedMspManufacturer={mspData.manufacturer}
            selectedMspLocation={mspData.location}
            selectedMspCadLocation={mspData.cad_location}
            selectedMspStyle={mspData.style}
            selectedMspType={mspData.type}
            mspNewExisting={mspData.msp_new_existing}
            mainBreakerNewExisting={mspData.main_breaker_new_existing}
            mspBussOptions={mspBusses}
            mspBreakerOptions={mspBreakers}
            mspManufacturerOptions={mspManufacturers}
            mspLocationOptions={mspLocations}
            mspCadLocationOptions={mspCadLocations}
            mspStyleOptions={mspStyles}
            mspTypeOptions={mspTypes}
            isLoading={!is_initialized}
            onMspBussSelect={(buss) => dispatch(updateMspData({ buss_rating: buss }))}
            onMspBreakerSelect={(breaker) => dispatch(updateMspData({ main_breaker_rating: breaker }))}
            onMspManufacturerSelect={(manufacturer) => dispatch(updateMspData({ manufacturer: manufacturer }))}
            onMspLocationSelect={(location) => dispatch(updateMspData({ location: location }))}
            onMspCadLocationSelect={(cadLocation) => dispatch(updateMspData({ cad_location: cadLocation }))}
            onMspStyleSelect={(style) => dispatch(updateMspData({ style: style }))}
            onMspTypeSelect={(type) => dispatch(updateMspData({ type: type }))}
            onMspNewExistingChange={(value) => dispatch(updateMspData({ msp_new_existing: value }))}
            onMainBreakerNewExistingChange={(value) => dispatch(updateMspData({ main_breaker_new_existing: value }))}
            isEnabled={isEnabled}
          />
            </div>
        </div>

        <div className="grid grid-rows-[auto_minmax(0,1fr)] gap-6 h-full">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-amber-400 overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-amber-100/50 border-b border-slate-200/60">
                <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">POI</span>
              </div>
              <div className="p-5">
                <POISelector
                  selectedPoi={poi.find(p => p.model === poiData.selected_poi)}
                  selectedPoiLocation={poiData.location}
                  selectedPoiBreakerSize={poiData.breaker_size?.toString() || ''}
                  onPoiSelect={(poi) => dispatch(updatePoiData({ selected_poi: poi?.model }))}
                  onPoiLocationSelect={(location) => dispatch(updatePoiData({ location: location }))}
                  onPoiBreakerSizeChange={(size) => {
                    const numericSize = size ? parseInt(size, 10) : undefined;
                    dispatch(updatePoiData({ breaker_size: numericSize }));
                  }}
                  poiOptions={poi}
                  poiLocations={poiLocations}
                  isLoading={!is_initialized}
                  isEnabled={isEnabled}
                />
              </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-amber-400 overflow-hidden h-full flex flex-col">
              <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-amber-100/50 border-b border-slate-200/60">
                <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">ATS</span>
              </div>
              <div className="p-5 flex-1">
                <ATSSelector
                  onAtsBussSelect={(buss) => dispatch(updateAtsData({ buss_rating: buss }))}
                  onAtsMainBreakerRatingSelect={(breaker) => dispatch(updateAtsData({ main_breaker_rating: breaker }))}
                  onAtsNewExistingChange={(value) => dispatch(updateAtsData({ ats_new_existing: value }))}
                  onAtsMainBreakerNewExistingChange={(value) => dispatch(updateAtsData({ main_breaker_new_existing: value }))}
                  onAtsPanelNameChange={(value) => dispatch(updateAtsData({ panel_name: value }))}
                  onAtsGeneratorNameChange={(value) => dispatch(updateAtsData({ generator_name: value }))}
                  selectedAtsBuss={atsData.buss_rating}
                  selectedAtsMainBreakerRating={atsData.main_breaker_rating}
                  atsNewExisting={atsData.ats_new_existing}
                  atsMainBreakerNewExisting={atsData.main_breaker_new_existing}
                  atsPanelName={atsData.panel_name}
                  atsGeneratorName={atsData.generator_name}
                  atsBussOptions={mspBusses}
                  atsMainBreakerRatingOptions={mspBreakers}
                  isLoading={!is_initialized}
                  isEnabled={isEnabled}
                />
              </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-amber-400 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-amber-100/50 border-b border-slate-200/60">
              <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12h18M3 6h18M3 18h18" />
              </svg>
              <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">MBE</span>
            </div>
            <div className="p-5">
          <MBESelector
            selectedMbeBuss={mbeData.buss_rating}
            selectedMbeMainBreakerRating={mbeData.main_breaker_rating}
            mbeNewExisting={mbeData.mbe_new_existing}
            mbeMainBreakerNewExisting={mbeData.main_breaker_new_existing}
            mbeBussOptions={mspBusses}
            mbeMainBreakerRatingOptions={mspBreakers}
            isLoading={!is_initialized}
            onMbeBussSelect={(buss) => dispatch(updateMbeData({ buss_rating: buss }))}
            onMbeMainBreakerRatingSelect={(rating) => dispatch(updateMbeData({ main_breaker_rating: rating }))}
            onMbeNewExistingChange={(value) => dispatch(updateMbeData({ mbe_new_existing: value }))}
            onMbeMainBreakerNewExistingChange={(value) => dispatch(updateMbeData({ main_breaker_new_existing: value }))}
            isEnabled={isEnabled}
          />
            </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-amber-400 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-amber-100/50 border-b border-slate-200/60">
              <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4h16v6H4zM4 14h16v6H4z" />
              </svg>
              <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">BLP</span>
            </div>
            <div className="p-5">
          <BLPSelector
            onBlpBussSelect={(buss) => dispatch(updateBlpData({ buss_rating: buss }))}
            onBlpMainBreakerRatingSelect={(breaker) => dispatch(updateBlpData({ main_breaker_rating: breaker }))}
            onBlpNewExistingChange={(value) => dispatch(updateBlpData({ blp_new_existing: value }))}
            onBlpMainBreakerNewExistingChange={(value) => dispatch(updateBlpData({ main_breaker_new_existing: value }))}
            selectedBlpBuss={blpData.buss_rating}
            selectedBlpMainBreakerRating={blpData.main_breaker_rating}
            blpNewExisting={blpData.blp_new_existing}
            blpMainBreakerNewExisting={blpData.main_breaker_new_existing}
            blpBussOptions={mspBusses}
            blpMainBreakerRatingOptions={mspBreakers}
            isLoading={!is_initialized}
            isEnabled={isEnabled}
          />
            </div>
        </div>

        <div className="xl:col-span-2 grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-amber-400 overflow-hidden h-full">
              <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-amber-100/50 border-b border-slate-200/60">
                <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7h16M4 12h16M4 17h16" />
                </svg>
                <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Subpanel</span>
              </div>
              <div className="p-5 h-full flex flex-col">
                <div className="flex-1">
                <SubpanelSelector
                  onSubpanelSizeSelect={(config) => dispatch(updateSubpanelData({ subpanel_size: config }))}
                  onMainBreakerRatingSelect={(rating) => dispatch(updateSubpanelData({ main_breaker_rating: rating }))}
                  onFeedingMethodSelect={(config) => dispatch(updateSubpanelData({ feeding_method: config }))}
                  onOcpdSizeSelect={(config) => dispatch(updateSubpanelData({ ocpd_size: config }))}
                  onPvBreakerSizeSelect={(config) => dispatch(updateSubpanelData({ pv_breaker_size: config }))}
                  onPvBreakerLocationSelect={(config) => dispatch(updateSubpanelData({ pv_breaker_location: config }))}
                  onSubpanelNewExistingChange={(value) => dispatch(updateSubpanelData({ subpanel_new_existing: value }))}
                  onMainBreakerNewExistingChange={(value) => dispatch(updateSubpanelData({ main_breaker_new_existing: value }))}
                  selectedSubpanelSize={subpanelData.subpanel_size}
                  selectedMainBreakerRating={subpanelData.main_breaker_rating}
                  selectedFeedingMethod={subpanelData.feeding_method}
                  selectedOcpdSize={subpanelData.ocpd_size}
                  selectedPvBreakerSize={subpanelData.pv_breaker_size}
                  selectedPvBreakerLocation={subpanelData.pv_breaker_location}
                  subpanelNewExisting={subpanelData.subpanel_new_existing}
                  mainBreakerNewExisting={subpanelData.main_breaker_new_existing}
                  electricConfigs={electricConfigs}
                  isLoading={!is_initialized}
                  isEnabled={isEnabled}
                />
                </div>
              </div>
          </div>

          <div className="flex flex-col gap-6 h-full">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-amber-400 overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-amber-100/50 border-b border-slate-200/60">
                  <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Utility Config</span>
                </div>
                <div className="p-5">
                  <UtilitySelector
                    onServiceFeedSourceSelect={(value) => dispatch(updateUtilityData({ service_feed_source: value }))}
                    onDistanceFromUmCalloutChange={(value) => dispatch(updateUtilityData({ distance_from_um_callout: value }))}
                    onUtilityMeterBaseNewExistingChange={(value) => dispatch(updateUtilityData({ utility_meter_base_new_existing: value }))}
                    onUtilityMeterBaseNameModelChange={(value) => dispatch(updateUtilityData({ utility_meter_base_name_model: value }))}
                    selectedServiceFeedSource={utilityData.service_feed_source}
                    distanceFromUmCallout={utilityData.distance_from_um_callout}
                    utilityMeterBaseNewExisting={utilityData.utility_meter_base_new_existing}
                    utilityMeterBaseNameModel={utilityData.utility_meter_base_name_model}
                    isLoading={!is_initialized}
                    isEnabled={isEnabled}
                  />
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-amber-400 overflow-hidden flex-1">
                <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-amber-100/50 border-b border-slate-200/60">
                  <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 19h16M4 15l4-4 4 4 4-4 4 4" />
                  </svg>
                  <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Production Meter</span>
                </div>
                <div className="p-5 h-full flex flex-col">
                  <div className="flex-1">
                    <ProductionMeterSelector
                      onProductionMeterNewExistingChange={(value) => dispatch(updateProductionMeterData({ new_existing: value }))}
                      onProductionMeterNameModelChange={(value) => dispatch(updateProductionMeterData({ meter_name_model: value }))}
                      productionMeterNewExisting={productionMeterData.new_existing}
                      productionMeterNameModel={productionMeterData.meter_name_model}
                      isLoading={!is_initialized}
                      isEnabled={isEnabled}
                    />
                  </div>
                </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-amber-400 overflow-hidden lg:col-span-2">
            <div className="flex items-center justify-between px-5 py-3 3xl:px-6 3xl:py-3.5 bg-amber-100/50 border-b border-slate-200/60">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Disco</span>
                </div>
                <div className="text-[10px] 3xl:text-xs">
                  {enabledDiscos.length >= 5 ? (
                    <span className="text-emerald-600 font-medium">5/5 configured</span>
                  ) : (
                    <span className="text-amber-600 font-medium">{enabledDiscos.length}/5 configured</span>
                  )}
                </div>
              </div>
              {enabledDiscos.length > 0 && enabledDiscos.length < 5 && (
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      const lastDiscoIndex = enabledDiscos[enabledDiscos.length - 1] - 1;
                      const lastDiscoData = [disco1Data, disco2Data, disco3Data, disco4Data, disco5Data][lastDiscoIndex];
                      if (lastDiscoData && lastDiscoData.rating && lastDiscoData.type && lastDiscoData.model) {
                        const nextDiscoNumber = [1, 2, 3, 4, 5].find(num => !enabledDiscos.includes(num));
                        if (nextDiscoNumber) {
                          setEnabledDiscos([...enabledDiscos, nextDiscoNumber].sort((a, b) => a - b));
                          dispatch(updateDiscoData({
                            disco_number: nextDiscoNumber as 1 | 2 | 3 | 4 | 5,
                            data: {
                              rating: lastDiscoData.rating,
                              type: lastDiscoData.type,
                              model: lastDiscoData.model,
                              fuses: lastDiscoData.fuses,
                              location: lastDiscoData.location,
                              text: lastDiscoData.text
                            }
                          }));
                        }
                      }
                    }}
                    disabled={(() => {
                      const lastDiscoIndex = enabledDiscos[enabledDiscos.length - 1] - 1;
                      const lastDiscoData = [disco1Data, disco2Data, disco3Data, disco4Data, disco5Data][lastDiscoIndex];
                      return !lastDiscoData?.rating || !lastDiscoData?.type || !lastDiscoData?.model;
                    })()}
                    className="text-[10px] 3xl:text-xs py-0.5 px-2 font-medium text-amber-700 hover:bg-amber-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    + Copy & Create
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const nextDiscoNumber = [1, 2, 3, 4, 5].find(num => !enabledDiscos.includes(num));
                      if (nextDiscoNumber) {
                        setEnabledDiscos([...enabledDiscos, nextDiscoNumber].sort((a, b) => a - b));
                      }
                    }}
                    className="text-[10px] 3xl:text-xs py-0.5 px-2 font-medium text-amber-700 bg-amber-200/60 hover:bg-amber-200 rounded-full transition-colors duration-200"
                  >
                    + Add New
                  </button>
                </div>
              )}
            </div>
            <div className="p-5">
          <div className="h-full flex flex-col">

            <div className="flex-1 overflow-y-auto space-y-4 min-h-0" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
              {enabledDiscos.map((discoNumber) => {
                const discoData = [disco1Data, disco2Data, disco3Data, disco4Data, disco5Data][discoNumber - 1];

                const handleCopyFromPrevious = () => {
                  if (discoNumber > 1) {
                    const previousDiscoNumber = discoNumber - 1;
                    const previousDiscoData = [disco1Data, disco2Data, disco3Data, disco4Data, disco5Data][previousDiscoNumber - 1];
                    dispatch(updateDiscoData({
                      disco_number: discoNumber as 1 | 2 | 3 | 4 | 5,
                      data: {
                        rating: previousDiscoData.rating,
                        type: previousDiscoData.type,
                        model: previousDiscoData.model,
                        fuses: previousDiscoData.fuses,
                        location: previousDiscoData.location,
                        text: previousDiscoData.text
                      }
                    }));
                  }
                };

                const handleDelete = () => {
                  dispatch(updateDiscoData({
                    disco_number: discoNumber as 1 | 2 | 3 | 4 | 5,
                    data: {
                      rating: undefined,
                      type: undefined,
                      model: undefined,
                      fuses: undefined,
                      location: undefined,
                      text: undefined
                    }
                  }));
                  setEnabledDiscos(enabledDiscos.filter(num => num !== discoNumber));
                };

                return (
                  <DISCOSelector
                    key={discoNumber}
                    discoNumber={discoNumber as 1 | 2 | 3 | 4 | 5}
                    discoData={discoData}
                    isEnabled={isEnabled}
                    canAddNew={false}
                    onCopyFromPrevious={handleCopyFromPrevious}
                    onDelete={handleDelete}
                    ratings={Array.from(new Set(disco.map(d => d.discoRating))).sort((a, b) => a - b)}
                    types={Array.from(new Set(disco.map(d => d.type)))}
                    availableModels={disco}
                    discoDetails={discoDetails}
                    isLoading={!is_initialized}
                  />
                );
              })}
            </div>
          </div>
            </div>
        </div>
      </div>
    </div>
  );
};
